<template>
  <Dialog 
    :visible="visible" 
    :modal="true" 
    :closable="true"
    :draggable="false"
    :style="{ width: '90vw', maxWidth: '1200px' }"
    class="game-details-modal"
    @update:visible="$emit('close')"
  >
    <template #header>
      <div class="modal-header">
        <Gamepad2 :size="24" color="#333333" />
        <h2>{{ title }}</h2>
      </div>
    </template>

    <div class="game-details-content">
      <!-- Gameplay Video -->
      <div v-if="metadata.gameplay_video && metadata.gameplay_video.content" class="video-section">
        <h3 class="section-title">Gameplay Video</h3>
        <div class="video-container">
          <iframe
            :src="getEmbedUrl(metadata.gameplay_video.content)"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
          ></iframe>
        </div>
      </div>

      <!-- Game Information Grid -->
      <div class="info-grid">
        <div v-if="metadata.price" class="info-item">
          <span class="info-label">Price</span>
          <span class="info-value">{{ formatPrice(metadata.price, metadata.currency) }}</span>
        </div>
        
        <div v-if="metadata.release_date" class="info-item">
          <span class="info-label">Release Date</span>
          <span class="info-value">{{ formatReleaseDate(metadata.release_date) }}</span>
        </div>
        
        <div v-if="metadata.median_playtime_hours" class="info-item">
          <span class="info-label">Median Playtime</span>
          <span class="info-value">{{ metadata.median_playtime_hours.content }} hours</span>
        </div>
        
        <div v-if="hasGameBrainRating" class="info-item">
          <span class="info-label">GameBrain Rating</span>
          <span class="info-value rating-value">{{ formatRating(metadata.gamebrain_rating) }}</span>
        </div>
        
        <div v-if="hasGameBrainRating" class="info-item">
          <span class="info-label">Review Count</span>
          <span class="info-value">{{ formatReviewCount(metadata.gamebrain_rating_count) }}</span>
        </div>
        
        <div v-else-if="metadata.rating" class="info-item">
          <span class="info-label">User Rating</span>
          <span class="info-value rating-value">{{ formatRating(metadata.rating) }}</span>
        </div>
        
        <div v-if="metadata.rating_count && !hasGameBrainRating" class="info-item">
          <span class="info-label">Review Count</span>
          <span class="info-value">{{ formatReviewCount(metadata.rating_count) }}</span>
        </div>
        
        <!-- Genres -->
        <div v-if="metadata.genres && metadata.genres.content && metadata.genres.content.length > 0" class="info-item">
          <span class="info-label">Genres</span>
          <span class="info-value">{{ formatArrayToString(metadata.genres.content) }}</span>
        </div>

        <!-- Play Modes -->
        <div v-if="metadata.play_modes && metadata.play_modes.content && metadata.play_modes.content.length > 0" class="info-item">
          <span class="info-label">Play Modes</span>
          <span class="info-value">{{ formatArrayToString(metadata.play_modes.content) }}</span>
        </div>
      </div>

      <!-- GameBrain Link -->
      <div v-if="metadata.gamebrain_url && metadata.gamebrain_url.content" class="gamebrain-link-section">
        <Button
          label="View on GameBrain"
          icon="pi pi-external-link"
          class="p-button-outlined gamebrain-link-button"
          @click="openGameBrainUrl"
        />
      </div>
    </div>
  </Dialog>
</template>

<script>
import { computed } from 'vue'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import { Gamepad2 } from 'lucide-vue-next'
import { formatDateLong } from '../utils/metadataFormatters.js'

export default {
  name: 'GameDetailsModal',
  components: {
    Dialog,
    Button,
    Gamepad2
  },
  props: {
    visible: {
      type: Boolean,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    metadata: {
      type: Object,
      required: true
    }
  },
  emits: ['close'],
  setup(props) {
    const hasGameBrainRating = computed(() => {
      return Boolean(
        props.metadata.gamebrain_rating?.content &&
        props.metadata.gamebrain_rating_count?.content
      )
    })

    const getEmbedUrl = (url) => {
      if (!url) return ''
      
      // Convert YouTube URLs to embed format
      if (url.includes('youtube.com') || url.includes('youtu.be')) {
        let videoId = ''
        
        if (url.includes('watch?v=')) {
          videoId = url.split('watch?v=')[1]?.split('&')[0]
        } else if (url.includes('youtu.be/')) {
          videoId = url.split('youtu.be/')[1]?.split('?')[0]
        }
        
        if (videoId) {
          return `https://www.youtube.com/embed/${videoId}`
        }
      }
      
      return url
    }

    const formatPrice = (priceData, currencyData) => {
      if (!priceData || typeof priceData.content !== 'number') return ''
      
      const price = priceData.content
      const currency = currencyData?.content || 'USD'
      
      return `${currency} ${price.toFixed(2)}`
    }

    const formatReleaseDate = (dateData) => {
      if (!dateData || !dateData.content) return ''
      
      return formatDateLong(dateData.content)
    }

    const formatRating = (ratingData) => {
      if (!ratingData || typeof ratingData.content !== 'number') return ''
      
      const rating = ratingData.content
      
      // Always show as percentage (rating is < 1)
      if (rating < 1) {
        return `${Math.round(rating * 100)}%`
      }
      
      return rating.toFixed(1)
    }

    const formatReviewCount = (countData) => {
      if (!countData || typeof countData.content !== 'number') return ''
      
      const count = countData.content
      
      if (count >= 1000000) {
        return `${(count / 1000000).toFixed(1)}M reviews`
      }
      
      if (count >= 1000) {
        return `${(count / 1000).toFixed(1)}K reviews`
      }
      
      return `${count.toLocaleString()} reviews`
    }

    const openGameBrainUrl = () => {
      if (props.metadata.gamebrain_url?.content) {
        window.open(props.metadata.gamebrain_url.content, '_blank')
      }
    }

    const formatArrayToString = (arr) => {
      if (!Array.isArray(arr) || arr.length === 0) return ''
      return arr.join(', ')
    }

    return {
      hasGameBrainRating,
      getEmbedUrl,
      formatPrice,
      formatReleaseDate,
      formatRating,
      formatReviewCount,
      openGameBrainUrl,
      formatArrayToString
    }
  }
}
</script>

<style scoped>
.modal-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
}

.game-details-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/* Video Section */
.video-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.video-container {
  position: relative;
  width: 100%;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  background: var(--bg-primary);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px var(--shadow-light);
}

.video-container iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 8px;
}

/* Info Grid */
.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.75rem;
  background: var(--bg-primary);
  border-radius: 8px;
  border: 1px solid var(--border-color);
}

.info-item.full-width {
  grid-column: 1 / -1;
}

.info-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 1rem;
  font-weight: 500;
  color: var(--text-primary);
}

.info-value.rating-value {
  color: #333333;
  font-weight: 700;
  font-size: 1.1rem;
}

/* GameBrain Link Section */
.gamebrain-link-section {
  display: flex;
  justify-content: center;
  padding-top: 0.5rem;
}

.gamebrain-link-button {
  width: 100%;
  max-width: 300px;
  border-color: #333333 !important;
  color: #333333 !important;
  font-weight: 600;
}

.gamebrain-link-button:hover {
  background: #333333 !important;
  color: white !important;
}

/* Dark theme: the #333333 rating text and button chrome are illegible on
   dark (token-based surfaces flip on their own). */
:root.dark-theme .info-value.rating-value {
  color: var(--text-secondary);
}

:root.dark-theme .gamebrain-link-button {
  border-color: var(--text-secondary) !important;
  color: var(--text-secondary) !important;
}

:root.dark-theme .gamebrain-link-button:hover {
  background: var(--text-secondary) !important;
  color: var(--bg-primary) !important;
}

/* Responsive */
@media (max-width: 768px) {
  .game-details-modal :deep(.p-dialog) {
    width: 95vw;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }

  .modal-header h2 {
    font-size: 1.25rem;
  }
}
</style>
